import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Label,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Input,
  Button
} from "@libs/zapi-ui/shadcn";
import { useState } from "react";
import { sendInvite } from "../services/sendInvite";
import { z } from "zod";
import { toast } from "sonner";
import { UserRoleSchema } from "@libs/shared-permissions";
import { getMemberRoles } from "../const/const";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { AxiosError } from "axios";
import { tryCatch } from "@/zapi-frontend/shared/lib/tryCatch";
interface InviteUserModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  organizationId: number;
  onSuccess: () => void;
}

export const formSchema = z.object({
  role: UserRoleSchema.refine((val) => val, { message: "Role is required" }),
  firstName: z
    .string()
    .min(1, { message: "First name is required" })
    .min(2, { message: "First name must be at least 2 characters" }),
  lastName: z
    .string()
    .min(1, { message: "Last name is required" })
    .min(2, { message: "Last name must be at least 2 characters" }),
  email: z.string({ message: "Email is required" }).email({ message: "Invalid email address" })
});

export function InviteUserModal({ isOpen, onOpenChange, organizationId, onSuccess }: InviteUserModalProps) {
  const { role: currentUserRole } = useMeStore();
  const [formData, setFormData] = useState<{
    role: string;
    firstName: string;
    lastName: string;
    email: string;
  }>({
    role: "",
    firstName: "",
    lastName: "",
    email: ""
  });

  function handleChange(field: keyof typeof formData, value: string) {
    setFormData({ ...formData, [field]: value });
    setErrors({ ...errors, [field]: undefined });
  }

  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{
    role?: string[];
    firstName?: string[];
    lastName?: string[];
    email?: string[];
  }>({});

  const handleClose = () => {
    setIsLoading(false);
    onOpenChange(false);
    setFormData({
      role: "",
      firstName: "",
      lastName: "",
      email: ""
    });
    setErrors({});
  };

  const handleSubmit = async () => {
    const validatedFields = formSchema.safeParse(formData);
    if (!validatedFields.success) {
      setErrors(validatedFields.error.flatten().fieldErrors);
      toast.error("Invalid form data. Fix errors and try again.");
      return;
    }

    const data = {
      orgId: organizationId.toString(),
      email: formData.email,
      role: formData.role,
      firstName: formData.firstName,
      lastName: formData.lastName
    };

    setIsLoading(true);
    const [, error] = await tryCatch<null, AxiosError<{ message: string }>>(sendInvite(data));
    handleClose();
    setIsLoading(false);
    if (error) {
      toast.error(error.response?.data.message || "Failed to send invitation");
      console.error("Error sending invite:", error);
      return;
    }

    onSuccess();
    toast.success("Invitation sent successfully");
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[525px]">
        <DialogHeader>
          <DialogTitle className="font-medium">Invite a member to this organization</DialogTitle>
        </DialogHeader>
        <div className="grid gap-6 py-4">
          <div className="grid gap-2">
            <Label htmlFor="member-role">Member Role</Label>
            <Select value={formData.role} onValueChange={handleChange.bind(null, "role")}>
              <SelectTrigger id="member-role" className={errors.role ? "border-red-500" : ""}>
                <SelectValue placeholder="Select Option" />
              </SelectTrigger>
              <SelectContent>
                {getMemberRoles(currentUserRole).map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-red-500 -mt-2">{errors.role?.[0]}</p>
          </div>

          <div className="flex gap-4">
            <div className="flex flex-col gap-2 basis-1/2">
              <Label htmlFor="first-name">First Name</Label>
              <Input
                id="first-name"
                placeholder="Enter first name"
                value={formData.firstName}
                onChange={(e) => handleChange("firstName", e.target.value)}
                inputError={errors.firstName?.[0]}
              />
            </div>
            <div className="flex flex-col gap-2 basis-1/2">
              <Label htmlFor="last-name">Last Name</Label>
              <Input
                id="last-name"
                placeholder="Enter last name"
                value={formData.lastName}
                onChange={(e) => handleChange("lastName", e.target.value)}
                inputError={errors.lastName?.[0]}
              />
            </div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              type="email"
              placeholder="Enter email address"
              value={formData.email}
              onChange={(e) => handleChange("email", e.target.value)}
              inputError={errors.email?.[0]}
            />
          </div>
        </div>
        <DialogFooter className="!justify-start">
          <Button
            type="submit"
            onClick={handleSubmit}
            className="w-full sm:w-auto"
            disabled={isLoading}
            isLoading={isLoading}
          >
            Send Invitation{" "}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
